<script setup lang="ts">
defineProps<{ forcePreview?: boolean }>();
const store = useSongStore();
const song = computed(() => store.data);
const showAllLyrics = ref(false);
const { t } = useI18n();

const sectionTitle = computed(() => {
  if (!song.value) {
    return t("S.LIT_Lyrics");
  }

  if (!song.value.lyrics?.length) {
    return t("S.LIT_LyricsPreview");
  }

  return t("S.LIT_Lyrics");
});

function onCopyLyrics() {
  store.onLyricsActivityDebounced("copied");

  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({
    event: "userLyricsCopy",
    song_number: song.value?.ccliSongNumber,
  });

  // Clear out event variables
  (window as any).dataLayer.push({
    song_number: undefined,
  });
}

const noLyrics = computed(() => {
  if (!song.value) {
    return false;
  }
  return !song.value.products.lyrics.exists;
});
</script>

<template>
  <section>
    <SectionTitle :placeholder="store.placeholderNotLoaded">
      {{ sectionTitle }}
      <CopyIcon
        v-if="song && song.lyrics?.length && !store.placeholderNotAuthorized"
        id="generalCopyLyricsButton"
        :value="store.copyLyricsContent"
        data-user-activity="Copied Lyrics"
        @click="onCopyLyrics"
      />
    </SectionTitle>
    <div
      v-if="store.placeholderNotAuthorized"
      class="lyrics-provided"
    >
      <div
        class="lg:block"
        :class="showAllLyrics ? 'block' : 'hidden'"
      >
        <div
          v-for="l of 3"
          :key="'placeholder' + l"
          class="lyric-section"
        >
          <h3>
            <span
              class="placeholder"
              style="width: 100px"
            />
          </h3>
          <Placeholder />
        </div>
      </div>

      <div
        v-if="song?.lyricsPreview"
        class="lyric-section lg:hidden"
        :class="{ hidden: showAllLyrics }"
      >
        <Placeholder />
      </div>
    </div>
    <div
      v-else-if="song?.lyrics?.length"
      class="lyrics-provided"
      @copy="onCopyLyrics"
    >
      <div
        :class="{
          'lg:block': !forcePreview,
          hidden: !showAllLyrics,
        }"
      >
        <div
          v-for="(l, i) of song.lyrics"
          :key="i"
          class="lyric-section"
        >
          <h3>{{ l.partLabel }}</h3>
          <div
            v-for="(ll, ii) of l.lyrics.split('|')"
            :key="i + 'l' + ii"
          >
            {{ ll }}
          </div>
        </div>
      </div>

      <div
        v-if="song?.lyricsPreview"
        class="lyric-section"
        :class="{
          hidden: showAllLyrics,
          'lg:hidden': !forcePreview,
        }"
      >
        <div
          v-for="(l, i) of (song.lyricsPreview as string)
            .split('|')
            .map((l) => l.trim())"
          :key="i"
        >
          {{ l }}
        </div>
        <button
          type="button"
          class="link text-primary"
          @click.stop.prevent="showAllLyrics = true"
        >
          {{ $t("SV.LIT_ShowAllLyrics") }}
        </button>
      </div>
    </div>
    <div
      v-else-if="song?.lyricsPreview"
      class="lyric-section"
    >
      <div
        v-for="(l, i) of (song.lyricsPreview as string)
          .split('|')
          .map((l) => l.trim())"
        :key="i"
      >
        {{ l }}
      </div>
    </div>
    <div v-if="noLyrics">{{ $t("S.MES_SorryWeDontHaveLyrics") }}</div>
  </section>
</template>

<style scoped lang="postcss">
h3 {
  @apply text-lg font-semibold;
}
.lyric-section {
  @apply mb-3;
}
</style>
